'use client'

import * as React from 'react'

import cn from '@config/tailwind/cn'

import { Sheet as SheetComponent, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../components'
import { useSidebar } from './context'

interface SidebarProps extends React.ComponentProps<'div'> {
  side?: 'left' | 'right'
  collapsible?: 'offcanvas' | 'icon' | 'none'
}

function Sidebar({ side = 'left', collapsible = 'offcanvas', className, children, ...props }: SidebarProps) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar()

  if (collapsible === 'none') {
    return (
      <div
        data-slot='sidebar'
        className={cn('ui:flex ui:h-full ui:w-(--sidebar-width) ui:flex-col ui:bg-linear-to-b', className)}
        {...props}
      >
        {children}
      </div>
    )
  }

  if (isMobile) {
    return (
      <SheetComponent open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          side={side}
          data-slot='sidebar'
          data-mobile='true'
          className={cn('ui:w-[18rem] ui:bg-linear-to-b ui:p-0 ui:[&>button]:hidden', className)}
        >
          <SheetHeader className='ui:sr-only'>
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Navigation sidebar.</SheetDescription>
          </SheetHeader>
          <div className='ui:flex ui:h-full ui:w-full ui:flex-col'>{children}</div>
        </SheetContent>
      </SheetComponent>
    )
  }

  return (
    <div
      className='ui:group/sidebar ui:hidden ui:md:block'
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-side={side}
      data-slot='sidebar'
    >
      {/* Gap div - takes up space in document flow to offset main content */}
      <div
        data-slot='sidebar-gap'
        className={cn(
          'ui:relative ui:w-(--sidebar-width) ui:bg-transparent ui:transition-[width] ui:duration-200 ui:ease-linear',
          'ui:group-data-[collapsible=offcanvas]/sidebar:w-0',
          'ui:group-data-[collapsible=icon]/sidebar:w-(--sidebar-width-icon)',
        )}
      />

      {/* Fixed sidebar panel */}
      <div
        data-slot='sidebar-container'
        className={cn(
          'ui:fixed ui:inset-y-0 ui:z-10 ui:hidden ui:h-svh ui:w-(--sidebar-width) ui:flex-col ui:transition-[left,right,width] ui:duration-200 ui:ease-linear ui:md:flex',
          side === 'left' && 'ui:left-0',
          side === 'right' && 'ui:right-0',
          side === 'left' && 'ui:group-data-[collapsible=offcanvas]/sidebar:-left-(--sidebar-width)',
          side === 'right' && 'ui:group-data-[collapsible=offcanvas]/sidebar:-right-(--sidebar-width)',
          'ui:group-data-[collapsible=icon]/sidebar:w-(--sidebar-width-icon)',
          side === 'left' && 'ui:border-r',
          side === 'right' && 'ui:border-l',
          'ui:border-outline-variant/50',
        )}
        {...props}
      >
        <div
          data-slot='sidebar-inner'
          className={cn('ui:flex ui:size-full ui:flex-col ui:bg-linear-to-b ui:select-none', className)}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

export { Sidebar }
export type { SidebarProps }
